import React, { useEffect, useRef, useState } from 'react';
import { showErrorToast, showInfoToast } from '../../../utils/toastHelper';
import { exportProducts } from '../services/productsService';
import { useTranslation } from 'react-i18next';
import FileNames from '../../../constants/fileNames';

export default function ImportProductsModal({
  isOpen,
  onClose,
  handleImportProducts,
  importLoading,
  FBZ,
  LOB,
  importProgress,
  setImportProgress,
}) {
  const modalRef = useRef(null);
  const modalInstanceRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importedData, setImportedData] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false); // State for confirmation modal
  const fileInputRef = useRef(null);
  const { t } = useTranslation('Products');
  const { t: importT } = useTranslation('Import');
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type (CSV)
      if (
        !selectedFile.name.endsWith('.csv') &&
        !selectedFile.name.endsWith('.xlsx')
      ) {
        showErrorToast(importT('PLEASE_SELECT_VALID_FILE'));
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Clear the file input
        }
        setFile(null);
        return;
      }

      if (selectedFile.size > 5 * 1024 * 1024) {
        // 2MB
        showErrorToast(importT('FILE_SIZE_EXCEEDS_LIMIT'));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setFile(null);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      showInfoToast(importT('PLEASE_SELECT_FILE_FIRST'));
      return;
    }

    // Confirm import action
    handleConfirmation();
  };

  const confirmImport = async () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('FBZ', FBZ);
    formData.append('LOB', LOB);
    const response = await handleImportProducts(formData);
    if (response.success) {
      setImportedData(response.import_progress);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFile(null);
    setConfirmationOpen(false); // Close confirmation modal
  };

  const cancelImport = () => {
    setConfirmationOpen(false); // Close confirmation modal without importing
  };

  const handleDownloadSample = async () => {
    const response = await exportProducts(
      { sample: true, FBZ, LOB },
      FileNames.PRODUCTS_SAMPLE_IMPORT
    );
  };
  useEffect(() => {
    modalInstanceRef.current = new window.bootstrap.Modal(modalRef.current, {
      backdrop: 'static',
      keyboard: false,
    });

    return () => {
      modalInstanceRef.current.dispose();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      modalInstanceRef.current.show();
      setFile(null);
      setImportedData(null); // Reset importedData here
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      modalInstanceRef.current.hide();
    }
  }, [isOpen]);
  return (
    <div
      className="modal fade import-file"
      id="import-products"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
      aria-labelledby="staticBackdropLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="staticBackdropLabel">
              {importedData ? t('Import Details') : t('IMPORTFILE')}
            </h1>
            <button
              //   type="button"
              className="btn-close"
              //   data-bs-dismiss="modal"
              //   aria-label="Close"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = ''; // Clear the file input
                }
                setFile(null);
                setImportedData(null);
                // setImportProgress(null);
                setConfirmationOpen(false);
                onClose('import');
              }}
            ></button>
          </div>
          {confirmationOpen ? (
            <div className="modal-body export-import">
              <h5>
                {/* {`We are going to import in the ${FBZ === 0 ? 'LOB' : 'FBZ'} section. Please confirm.`} */}
                {t('IMPORTCONFIRMATION', {
                  section: FBZ === 0 ? 'LOB' : 'FBZ',
                })}
              </h5>
              <div className="d-flex justify-content-between">
                <button
                  onClick={confirmImport}
                  className={`import-btn mt-1 ${importLoading ? 'disable-btn' : ''}`}
                  disabled={importLoading}
                >
                  {t('YESPROCEED')}
                </button>
                <button onClick={cancelImport} className="cancel">
                  {t('NOCANCEL')}
                </button>
              </div>
            </div>
          ) : importedData ? (
            <div className="modal-body">
              <div className="product-inner">
                <div className="import-status mt-3">
                  <h3 className="import-head">{t('IMPORTSTATUS')}</h3>
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t('STATUS')}:</strong>{' '}
                        {t(importedData.status)}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t('PROCESSEDROWS')}:</strong>{' '}
                        {importedData.processed_rows}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t('SUCCESSFULROWS')}:</strong>{' '}
                        {importedData.success_rows}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t('FAILEDROWS')}:</strong>{' '}
                        {importedData.failed_rows}
                      </p>
                    </div>
                  </div>

                  {importedData.error_message && (
                    <div className="errors">
                      <h4>{t('ERRORS')}:</h4>
                      <ul>
                        {importedData.error_message
                          .split(',')
                          .map((error, index) => (
                            <li key={index}>{error.trim()}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onClose('import')}
                  className={`import-btn`}
                >
                  {t('CLOSE')}
                </button>
              </div>
            </div>
          ) : (
            <div className="modal-body">
              <div className="product-inner">
                <label>
                  <form action="">
                    <img src="images/file-download-import-icon 1.svg" alt="" />
                    <p>{t('UPLOADFILE')}...</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="file"
                      id="file"
                      onChange={handleFileChange}
                      accept=".xls,.xlsx,.csv"
                    />
                  </form>
                </label>
                {file && (
                  <div className="file-name-display mt-3">
                    <p>
                      <strong>{t('SELECTEDFILE')}:</strong> {file.name}
                    </p>
                  </div>
                )}
                <div className="file-name-display mt-3">
                  <p>
                    <button
                      className="btn btn-link"
                      onClick={handleDownloadSample}
                    >
                      {t('DOWNLOADSAMPLE')}
                    </button>
                  </p>
                </div>

                <div className="d-flex justify-content-between py-3 file-size">
                  <p className="mb-0">{t('SUPPORTEDFORMAT')}: xlsx</p>
                  <p className="mb-0">{t('MAXIMUMSIZE')}: 2MB</p>
                </div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`import-btn mt-1 ${importLoading ? 'disable-btn' : ''}`}
                  disabled={importLoading}
                >
                  {importLoading ? t('IMPORTING') + '...' : t('IMPORT')}
                </button>

                {/* {importProgress && (
                <div className="import-status mt-3">
                  <h3 className="import-head">{t("IMPORTSTATUS")}</h3>
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t("STATUS")}:</strong>{" "}
                        {importProgress.status}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t("PROCESSEDROWS")}:</strong>{" "}
                        {importProgress.processed_rows}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t("SUCCESSFULROWS")}:</strong>{" "}
                        {importProgress.success_rows}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong className="pe-2">{t("FAILEDROWS")}:</strong>{" "}
                        {importProgress.failed_rows}
                      </p>
                    </div>
                  </div>

                  {importProgress.error_message && (
                    <div className="errors">
                      <h4>{t("ERRORS")}:</h4>
                      <ul>
                        {importProgress.error_message
                          .split(",")
                          .map((error, index) => (
                            <li key={index}>{error.trim()}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )} */}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
